import axios, { type AxiosInstance } from 'axios';
import type ContactData from '@/chat/types/ContactData';
import { type EventSourceMessage, fetchEventSource } from '@microsoft/fetch-event-source';
import type StartChatResponse from '@/chat/types/StartChatResponse';
import type ChatSession from '@/chat/types/ChatSession';
import type GenerateChatTokenResponse from '@/chat/types/GenerateChatTokenResponse';
import type LoadChatResponse from '@/chat/types/LoadChatResponse';

export default class ChatClient {
    constructor(
        private client: AxiosInstance = axios.create({
            baseURL: import.meta.env.VITE_API_HOST + import.meta.env.VITE_API_BASEPATH,
        }),
    ) {}

    public async startChat(assistentId: string, contactData: ContactData): Promise<StartChatResponse> {
        const response = await this.client.post(`/ai/assistent/assistents/${assistentId}/start_chat`, {
            ...contactData,
            visitorId: sessionStorage.getItem('assistent_visitor_id'),
        }, {
            headers: {
                'Content-Type': 'application/json',
            },
        });

        return {
            chatId: response.data.chatId,
            hub: response.data.hub,
            topic: response.data.topic,
            token: response.data.token,
        };
    }

    public async generateChatToken(chatId: string): Promise<GenerateChatTokenResponse> {
        const response = await this.client.post(`/ai/chat/chats/${chatId}/generate_chat_token`, {}, {
            headers: {
                'Content-Type': 'application/json',
            },
        });

        return {
            chatId: response.data.chatId,
            hub: response.data.hub,
            topic: response.data.topic,
            token: response.data.token,
        };
    }

    public async loadChat(chatId: string): Promise<LoadChatResponse> {
        const response = await this.client.get(`/ai/chat/chats_anonymous/${chatId}`);

        return response.data as LoadChatResponse;
    }

    public async sendMessage(assistentId: string, chatId: string, message: string): Promise<void> {
        await this.client.post(`/ai/assistent/assistents/${assistentId}/send_message`, {
            message,
            chatId,
            url: window.location.href,
            pageTitle: window.document.title,
        }, {
            headers: {
                'Content-Type': 'application/json',
            },
        });
    }

    /** We do not await this call since it's something that we want to run async */
    // eslint-disable-next-line class-methods-use-this
    public async listenForMessages(
        session: ChatSession,
        onMessage: (message: EventSourceMessage) => Promise<void>,
        onClose: () => Promise<void>,
        onError: (error: any) => Promise<void>,
    ): Promise<void> {
        try {
            await fetchEventSource(session.topicUrl, {
                headers: {
                    Authorization: `Bearer ${session.token}`,
                },
                onmessage: onMessage,
                onerror: (error) => {
                    throw error;
                },
                onclose: onClose,
                openWhenHidden: true,
            });
        } catch (error) {
            await onError(error);
        }
    }

    public async cancelHandover(
        session: ChatSession,
    ): Promise<void> {
        await this.client.post(`/ai/chat/chats/${session.chatId}/cancel_handover`, {
            headers: {
                'Content-Type': 'application/json',
            },
        });
    }

    public async contactMeLater(
        session: ChatSession,
        form: Record<string, any>,
    ): Promise<void> {
        await this.client.post(`/ai/chat/chats/${session.chatId}/contact_me_later`, {
            handoverContactDataFields: form,
        }, {
            headers: {
                'Content-Type': 'application/json',
            },
        });
    }
}
